/**
 * Canonicalises a persisted `FilterGroup` onto the shape `types/filters`
 * declares (`expressions[]`), tolerating the legacy `conditions[]` key that
 * predates the rename.
 *
 * Why this exists: the platform arc templates seeded by schema-service
 * migrations (`20260302*_add_center_*_arc.js`) write
 * `calculatedConditions: { operator, conditions: [...] }`, while `FilterGroup`
 * declares `expressions`. Nothing normalized the two, so a legacy group reached
 * `filterEngine.evaluateGroup` with neither `expressions` nor `groups` and was
 * treated as an EMPTY group — which evaluates to `true`, i.e. every calculated
 * transition on those templates fired unconditionally — and reached
 * `referencedAspects`, which iterated `group.expressions` and threw
 * `TypeError: group.expressions is not iterable`.
 *
 * The same seeds also write their leaf operators in the condition-system
 * shorthand (`gte`, `gt`, `lt`, `lte`, `eq`, `neq`) rather than the canonical
 * `FilterOperator` names (`greater_than_equals`, ...). `compareValues` — the one
 * operator switch on the platform — only understands the canonical names, so a
 * leaf carrying an alias evaluated `false` unconditionally: once Phase 7.1 made
 * the seeded groups non-empty, every `elapsed_in_stage` clause on them (and any
 * template authored in the same shape) could never become true, the calculated
 * transition never fired, and the arc's wake re-armed at the same past moment
 * on every tick (EAINT-894 Phase 7.8). The alias vocabulary already lives in
 * `operators.ts` (`normalizeOperator`); this module applies it to every
 * property leaf so evaluation and wake computation agree on what a group says.
 *
 * The transform is a pure, deep, structure-preserving rewrite:
 *  - a group's `conditions[]` becomes `expressions[]` (an explicit
 *    `expressions` always wins; `conditions` is then dropped);
 *  - every property leaf's `operator` alias becomes its canonical name (lane
 *    leaves — `{ kind: ... }` — carry no `FilterOperator` and are untouched);
 *  - nested `groups[]` are normalized recursively;
 *  - a group with neither key normalizes to `expressions: []`, so an empty
 *    group can never throw.
 *
 * It is idempotent — normalizing an already-canonical group returns an
 * equivalent group — so it is safe to apply at more than one boundary.
 */
import { FilterGroup, FilterGroupLeaf } from 'types/filters';
import { normalizeOperator } from './operators';

/**
 * Canonicalises one leaf's `operator` alias. Returns the SAME reference when
 * the leaf is a lane leaf, carries no string operator, or is already canonical,
 * so callers can detect a rewrite by identity.
 */
function canonicalizeLeafOperator<T>(leaf: T): T {
  if (leaf === null || typeof leaf !== 'object' || 'kind' in (leaf as object)) return leaf;
  const operator = (leaf as { operator?: unknown }).operator;
  if (typeof operator !== 'string') return leaf;
  const canonical = normalizeOperator(operator);
  return canonical === operator ? leaf : ({ ...(leaf as object), operator: canonical } as T);
}

/** Maps `canonicalizeLeafOperator` over an array, returning the SAME array when nothing changed. */
function canonicalizeLeafOperators<T>(leaves: T[]): T[] {
  let mutated = false;
  const rewritten = leaves.map((leaf) => {
    const next = canonicalizeLeafOperator(leaf);
    if (next !== leaf) mutated = true;
    return next;
  });
  return mutated ? rewritten : leaves;
}

/** The persisted shape as it may arrive: `expressions` or the legacy `conditions`. */
interface RawFilterGroup {
  id?: string;
  operator?: 'and' | 'or';
  expressions?: FilterGroupLeaf[];
  /** Legacy alias for `expressions` — seeded platform templates still carry it. */
  conditions?: FilterGroupLeaf[];
  groups?: RawFilterGroup[];
}

/** A group's leaves: `expressions`, else the legacy `conditions` alias, else none. */
function leavesOf(group: RawFilterGroup): FilterGroupLeaf[] {
  if (Array.isArray(group.expressions)) return group.expressions;
  if (Array.isArray(group.conditions)) return group.conditions;
  return [];
}

export function normalizeFilterGroup(raw: FilterGroup | RawFilterGroup | null | undefined): FilterGroup {
  const group = (raw ?? {}) as RawFilterGroup;

  const expressions = canonicalizeLeafOperators(leavesOf(group));

  const normalized: FilterGroup = {
    ...(group.id !== undefined ? { id: group.id } : {}),
    operator: group.operator === 'or' ? 'or' : 'and',
    expressions,
  };

  if (Array.isArray(group.groups)) {
    normalized.groups = group.groups.map((nested) => normalizeFilterGroup(nested));
  }

  return normalized;
}

/**
 * The dotted path to the FIRST legacy `conditions[]` group inside `value` (any
 * JSON subtree), or `null` when there is none. Schema-service uses the path to
 * name the offending key in its 400 so an author can find it; the boolean
 * wrapper below is for callers that only need the yes/no.
 *
 * A node counts as a group only when it carries BOTH a string `operator` and an
 * array `conditions` — an unrelated `conditions` field never matches.
 */
export function findLegacyFilterGroupPath(value: unknown, path = ''): string | null {
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const found = findLegacyFilterGroupPath(value[i], `${path}[${i}]`);
      if (found) return found;
    }
    return null;
  }

  if (value === null || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  if (Array.isArray(record['conditions']) && typeof record['operator'] === 'string') {
    return path ? `${path}.conditions` : 'conditions';
  }

  for (const [key, child] of Object.entries(record)) {
    const found = findLegacyFilterGroupPath(child, path ? `${path}.${key}` : key);
    if (found) return found;
  }

  return null;
}

/**
 * True when `value` still carries a legacy `conditions[]` group anywhere inside
 * it. Used by the migration that rewrites the seeded templates.
 */
export function hasLegacyFilterGroupShape(value: unknown): boolean {
  return findLegacyFilterGroupPath(value) !== null;
}

/**
 * Deep, structure-preserving rewrite of every legacy `FilterGroup` inside an
 * arbitrary JSON subtree — a persisted arc-template `definition`, an
 * `applicability`, a stage intent's gates. A node is treated as a group only
 * when it carries BOTH a string `operator` and an array `conditions`, so an
 * unrelated `conditions` field is never touched.
 *
 * Returns the SAME reference when nothing changed, so callers can cheaply tell
 * a rewrite happened (`result !== value`).
 */
export function normalizeLegacyFilterGroupsDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    let mutated = false;
    const rewritten = value.map((item) => {
      const next = normalizeLegacyFilterGroupsDeep(item);
      if (next !== item) mutated = true;
      return next;
    });
    return (mutated ? rewritten : value) as T;
  }

  if (value === null || typeof value !== 'object') return value;

  const record = value as Record<string, unknown>;
  let mutated = false;
  const result: Record<string, unknown> = {};

  for (const [key, child] of Object.entries(record)) {
    const next = normalizeLegacyFilterGroupsDeep(child);
    if (next !== child) mutated = true;
    result[key] = next;
  }

  if (Array.isArray(result['conditions']) && typeof result['operator'] === 'string') {
    if (!Array.isArray(result['expressions'])) result['expressions'] = result['conditions'];
    delete result['conditions'];
    mutated = true;
  }

  // A group (string `operator` + `expressions[]`) canonicalises each property
  // leaf's operator alias. Leaves are not groups, so the child walk above left
  // them untouched — this is the one place a leaf's operator is rewritten.
  if (Array.isArray(result['expressions']) && typeof result['operator'] === 'string') {
    const leaves = result['expressions'] as unknown[];
    const canonical = canonicalizeLeafOperators(leaves);
    if (canonical !== leaves) {
      result['expressions'] = canonical;
      mutated = true;
    }
  }

  return (mutated ? result : value) as T;
}
